/**
 * Single source of truth for Cimfoni pricing.
 * Mirrors the Cimfoni Pricing & Service Agreement v3.0.
 * Both the in-app Billing page and the Leadership Manual read from here.
 *
 * Update this file when a new agreement version is signed — never hard-code
 * pricing anywhere else.
 */

export const AGREEMENT_VERSION = 'v3.0';

export type TierId = 'standard' | 'professional' | 'enterprise';

export interface PricingTier {
  id: TierId;
  name: string;
  /** Per-facility monthly price in USD. Null = contact for pricing. */
  price: number | null;
  /** One-time setup fee in USD. Null = contact for pricing. */
  setup: number | null;
  /** Max clients per facility. Null = custom. */
  clients: number | null;
  /** Included admin seats per facility. Null = custom. */
  adminSeats: number | null;
  /** Included staff seats per facility. Null = custom. */
  staffSeats: number | null;
  /** Short marketing tagline. */
  tagline: string;
  /** Bullet list shown on the tier card. */
  highlights: string[];
}

export const TIERS: PricingTier[] = [
  {
    id: 'standard',
    name: 'Standard',
    price: 899,
    setup: 750,
    clients: 80,
    adminSeats: 10,
    staffSeats: 20,
    tagline: 'For single facilities running core shelter operations.',
    highlights: [
      'Up to 80 clients per facility',
      '10 admin + 20 staff seats',
      'All core modules included',
      '99.5% uptime SLA',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 1200,
    setup: 1200,
    clients: 200,
    adminSeats: 15,
    staffSeats: 35,
    tagline: 'For larger facilities and growing programs.',
    highlights: [
      'Up to 200 clients per facility',
      '15 admin + 35 staff seats',
      'All core modules included',
      'Priority support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise / Custom',
    price: null,
    setup: null,
    clients: null,
    adminSeats: null,
    staffSeats: null,
    tagline: 'For multi-org networks with custom requirements.',
    highlights: [
      'Multi-organization support',
      'Custom SLA & security review',
      'White-glove onboarding',
      'Dedicated customer success manager',
    ],
  },
];

/** Monthly $/seat over the included tier allotment. */
export const SEAT_OVERAGE = {
  admin: 8,
  staff: 3,
} as const;

export interface AddOn {
  id: string;
  name: string;
  /** Monthly price per facility. Null = contact for pricing. */
  price: number | null;
  description: string;
}

export const ADDONS: AddOn[] = [
  {
    id: 'ai-shift-assistant',
    name: 'AI Shift Assistant',
    price: 99,
    description: 'AI-powered chat support for shift handoffs, incident drafts, and report generation.',
  },
  {
    id: 'operations-analytics',
    name: 'Operations Analytics (Executive Dashboard)',
    price: 79,
    description: 'Advanced KPIs, trend charts, occupancy rates, and compliance metrics for leadership.',
  },
  {
    id: 'inspections-compliance',
    name: 'Inspections & Compliance',
    price: 59,
    description: 'Structured inspection workflows, checklist templates, and audit compliance tracking.',
  },
  {
    id: 'missing-child-reports',
    name: 'Missing Child Reports',
    price: 49,
    description: 'Specialized workflow for documenting and escalating missing minor incidents.',
  },
  {
    id: 'training-development',
    name: 'Training & Development',
    price: 49,
    description: 'Document assignments, acknowledgments, and staff training completion tracking.',
  },
];

export const ANNUAL_DISCOUNT = '12 months for the price of 11';

export const BILLING_TERMS = [
  'Annual option: 12 months for the price of 11',
  'Month-to-month: standard rate, 30-day written cancellation',
  'Invoices delivered 5 business days before due date',
  'Per-facility licensing under your Master Service Agreement',
];

export const CONTACT = {
  sales: 'hello@mitch.solutions',
  enterprise: 'enterprise@mitch.solutions',
  support: 'support@mitch.solutions',
  accountManagerName: 'Your Cimfoni representative',
} as const;

export const getTier = (id: TierId | string | null | undefined): PricingTier =>
  TIERS.find((t) => t.id === id) ?? TIERS.find((t) => t.id === 'professional')!;

export const formatPrice = (price: number | null): string =>
  price === null ? 'Contact' : `$${price.toLocaleString()}`;
